/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';

import Loader from 'components/atoms/loader';
import { useProposalShell } from 'components/organisms/ai_management_bidding_proposal/context';
import { useProposalCompany } from 'hooks/useProposalCompany';
import {
  CompanyBanco,
  CompanyCustomField,
  CompanyRepresentante,
  ProposalCompanyData,
} from 'services/actions/ai_management_proposal_company';
import { proposalFieldTarget } from 'services/ai_management_proposal';
import { Container } from './styles';

interface AiManagementProposalCompanyProps {
  biddingId: number;
  // Quem soma estas pendências com as das condições é o invólucro da etapa.
  onPendencies?: (count: number) => void;
  // O que falta, em texto, para a caixa "Para concluir esta etapa".
  onPendencyItems?: (itens: string[]) => void;
}

const maskCnpj = (value: string) => {
  const d = value.replace(/\D/g, '').slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

const maskCpf = (value: string) => {
  const d = value.replace(/\D/g, '').slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
};

const SIMPLE_FIELDS: Array<{ chave: string; rotulo: string; obrigatorio: boolean }> = [
  { chave: 'razao_social', rotulo: 'Razão social', obrigatorio: true },
  { chave: 'cnpj', rotulo: 'CNPJ', obrigatorio: true },
  { chave: 'inscricao_estadual', rotulo: 'Inscrição estadual', obrigatorio: false },
  { chave: 'telefone', rotulo: 'Telefone', obrigatorio: false },
  { chave: 'endereco', rotulo: 'Endereço', obrigatorio: true },
  { chave: 'email', rotulo: 'E-mail', obrigatorio: true },
];

const REPRESENTANTE_FIELDS: Array<{ chave: keyof CompanyRepresentante; rotulo: string }> = [
  { chave: 'nome', rotulo: 'Nome do representante' },
  { chave: 'cpf', rotulo: 'CPF' },
  { chave: 'rg', rotulo: 'RG' },
  { chave: 'cargo', rotulo: 'Cargo' },
];

const BANCO_FIELDS: Array<{ chave: keyof CompanyBanco; rotulo: string }> = [
  { chave: 'banco', rotulo: 'Banco' },
  { chave: 'agencia', rotulo: 'Agência' },
  { chave: 'conta', rotulo: 'Conta' },
];

const str = (value: unknown) => (typeof value === 'string' ? value : '');

const AiManagementProposalCompany: React.FC<AiManagementProposalCompanyProps> = ({
  biddingId,
  onPendencies,
  onPendencyItems,
}) => {
  const { trackSave, setPendencies } = useProposalShell();
  const { monitored, cnpj, setCnpj, company, loading, error, savedFlash, save, confirm } =
    useProposalCompany(biddingId, { trackSave });

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ProposalCompanyData>({});
  const [custom, setCustom] = useState<CompanyCustomField[]>([]);
  const [undoField, setUndoField] = useState<CompanyCustomField | null>(null);
  const [saveError, setSaveError] = useState('');

  const bancoExigido = company?.dados_bancarios_exigidos ?? true;

  // Banco só conta como pendência quando exigido.
  useEffect(() => {
    if (!company) return;
    const faltamObrigatorios = SIMPLE_FIELDS.filter((f) => f.obrigatorio).some(
      (f) => !str(company.dados[f.chave]).trim(),
    );
    // O representante assina a proposta: os quatro campos são obrigatórios na etapa.
    const faltamRepresentante = REPRESENTANTE_FIELDS.filter(
      (f) => !str(company.dados.representante_legal?.[f.chave]).trim(),
    );
    const faltamBanco =
      bancoExigido &&
      BANCO_FIELDS.some((f) => !str(company.dados.dados_bancarios?.[f.chave]).trim());
    const faltaConfirmar = !company.confirmada;
    const pend =
      (faltamObrigatorios || faltamRepresentante.length > 0 || faltamBanco ? 1 : 0) +
      (faltaConfirmar ? 1 : 0);
    if (onPendencies) onPendencies(pend);
    else setPendencies('empresa', pend);
    if (onPendencyItems) {
      const itens = SIMPLE_FIELDS.filter(
        (f) => f.obrigatorio && !str(company.dados[f.chave]).trim(),
      ).map((f) => `Preencher ${company.rotulos?.[f.chave] || f.rotulo} em Dados da empresa`);
      faltamRepresentante.forEach((f) =>
        itens.push(
          `Preencher ${
            f.chave === 'nome' ? 'nome do representante' : f.rotulo
          } em Dados da empresa`,
        ),
      );
      if (faltamBanco) itens.push('Preencher os dados bancários em Dados da empresa');
      if (faltaConfirmar) itens.push('Marcar “Confirmo que os dados da empresa estão corretos”');
      onPendencyItems(itens);
    }
  }, [company, bancoExigido, setPendencies, onPendencies, onPendencyItems]);

  const startEdit = () => {
    if (!company) return;
    setDraft(JSON.parse(JSON.stringify(company.dados)));
    setCustom(company.campos_personalizados.map((c) => ({ ...c })));
    setSaveError('');
    setEditing(true);
  };

  const setField = (chave: string, value: string) =>
    setDraft((prev) => ({ ...prev, [chave]: value }));

  const setRep = (chave: keyof CompanyRepresentante, value: string) =>
    setDraft((prev) => ({
      ...prev,
      representante_legal: {
        ...(prev.representante_legal as CompanyRepresentante),
        [chave]: value,
      },
    }));

  const setBanco = (chave: keyof CompanyBanco, value: string) =>
    setDraft((prev) => ({
      ...prev,
      dados_bancarios: { ...(prev.dados_bancarios as CompanyBanco), [chave]: value },
    }));

  const submit = () => {
    save({
      dados: draft,
      campos_personalizados: custom,
      rotulos: company?.rotulos,
      excluidos: company?.excluidos,
      salvar_no_perfil: true,
    }).then((err) => {
      if (err) setSaveError(err);
      else {
        setEditing(false);
        setSaveError('');
      }
    });
  };

  const addCustom = () =>
    setCustom((prev) => [
      ...prev,
      { id: `cp_${Date.now()}`, nome: '', valor: '', ordem: prev.length + 1 },
    ]);

  const removeCustom = (id: string) => {
    const removido = custom.find((c) => c.id === id);
    setCustom((prev) => prev.filter((c) => c.id !== id));
    if (removido) setUndoField(removido);
  };

  const undoRemoveCustom = () => {
    if (undoField) setCustom((prev) => [...prev, undoField]);
    setUndoField(null);
  };

  // Campo vazio: "Preencher" se obrigatório, "Não informado" se opcional (como no protótipo).
  const renderCampo = (key: string, rotulo: string, valor: string, obrigatorio: boolean) => (
    <div key={key}>
      <small>{rotulo}</small>
      {valor && <strong>{valor}</strong>}
      {!valor && obrigatorio && <span className="badge-preencher">Preencher</span>}
      {!valor && !obrigatorio && <span className="empty-optional">Não informado</span>}
    </div>
  );

  const renderLeitura = () => (
    <div className="company-grid">
      {SIMPLE_FIELDS.map((f) => {
        const texto = str(company?.dados[f.chave]).trim();
        return renderCampo(
          f.chave,
          company?.rotulos?.[f.chave] || f.rotulo,
          f.chave === 'cnpj' ? maskCnpj(texto) : texto,
          f.obrigatorio,
        );
      })}

      <h3
        className="company-subtitle"
        data-proposal-target={proposalFieldTarget('representante_legal')}
      >
        Representante legal
      </h3>
      {REPRESENTANTE_FIELDS.map((f) => {
        const bruto = str(company?.dados.representante_legal?.[f.chave]).trim();
        return renderCampo(f.chave, f.rotulo, f.chave === 'cpf' ? maskCpf(bruto) : bruto, true);
      })}

      <h3 className="company-subtitle">
        Dados bancários
        {!bancoExigido && <small className="optional-hint">Opcional: o edital não exige</small>}
      </h3>
      {BANCO_FIELDS.map((f) =>
        renderCampo(
          f.chave,
          f.rotulo,
          str(company?.dados.dados_bancarios?.[f.chave]).trim(),
          bancoExigido,
        ),
      )}

      {(company?.campos_personalizados ?? []).length > 0 && (
        <h3 className="company-subtitle">Campos personalizados</h3>
      )}
      {(company?.campos_personalizados ?? []).map((c) =>
        renderCampo(c.id, c.nome, (c.valor ?? '').trim(), false),
      )}
    </div>
  );

  const renderEdicao = () => (
    <>
      <div className="edit-grid">
        {SIMPLE_FIELDS.map((f) => (
          <label key={f.chave} className="proposal-company-edit field" htmlFor={`emp-${f.chave}`}>
            <span className="field-title">{f.rotulo}</span>
            <input
              id={`emp-${f.chave}`}
              type="text"
              className="form-control form-control-sm"
              value={f.chave === 'cnpj' ? maskCnpj(str(draft[f.chave])) : str(draft[f.chave])}
              onChange={(event) => {
                const { value } = event.target;
                setField(f.chave, f.chave === 'cnpj' ? value.replace(/\D/g, '') : value);
              }}
            />
          </label>
        ))}

        <h3
          className="company-subtitle"
          data-proposal-target={proposalFieldTarget('representante_legal')}
        >
          Representante legal
        </h3>
        {REPRESENTANTE_FIELDS.map((f) => (
          <label key={f.chave} className="proposal-company-edit field" htmlFor={`rep-${f.chave}`}>
            <span className="field-title">{f.rotulo}</span>
            <input
              id={`rep-${f.chave}`}
              type="text"
              className="form-control form-control-sm"
              value={
                f.chave === 'cpf'
                  ? maskCpf(str(draft.representante_legal?.[f.chave]))
                  : str(draft.representante_legal?.[f.chave])
              }
              onChange={(event) => {
                const { value } = event.target;
                setRep(f.chave, f.chave === 'cpf' ? value.replace(/\D/g, '') : value);
              }}
            />
          </label>
        ))}

        <h3 className="company-subtitle">
          Dados bancários
          {!bancoExigido && <small className="optional-hint">Opcional: o edital não exige</small>}
        </h3>
        {BANCO_FIELDS.map((f) => (
          <label key={f.chave} className="proposal-company-edit field" htmlFor={`banco-${f.chave}`}>
            <span className="field-title">{f.rotulo}</span>
            <input
              id={`banco-${f.chave}`}
              type="text"
              className="form-control form-control-sm"
              value={str(draft.dados_bancarios?.[f.chave])}
              onChange={(event) => {
                const { value } = event.target;
                setBanco(f.chave, value);
              }}
            />
          </label>
        ))}

        <h3 className="company-subtitle">Campos personalizados</h3>
        {custom.map((c, index) => (
          <div key={c.id} className="edit-grid-wide d-flex align-items-end">
            <label
              className="proposal-company-edit field flex-grow-1 mr-2 mb-0"
              htmlFor={`cp-nome-${c.id}`}
            >
              <span className="field-title">Nome</span>
              <input
                id={`cp-nome-${c.id}`}
                type="text"
                className="form-control form-control-sm"
                value={c.nome}
                onChange={(event) => {
                  const { value } = event.target;
                  setCustom((prev) =>
                    prev.map((x, i) => (i === index ? { ...x, nome: value } : x)),
                  );
                }}
              />
            </label>
            <label
              className="proposal-company-edit field flex-grow-1 mr-2 mb-0"
              htmlFor={`cp-valor-${c.id}`}
            >
              <span className="field-title">Valor</span>
              <input
                id={`cp-valor-${c.id}`}
                type="text"
                className="form-control form-control-sm"
                value={c.valor}
                onChange={(event) => {
                  const { value } = event.target;
                  setCustom((prev) =>
                    prev.map((x, i) => (i === index ? { ...x, valor: value } : x)),
                  );
                }}
              />
            </label>
            <button
              type="button"
              className="btn btn-sm btn-link text-danger p-0 mb-2"
              onClick={() => removeCustom(c.id)}
            >
              Excluir
            </button>
          </div>
        ))}
        <div className="edit-grid-wide">
          <button
            type="button"
            className="btn btn-sm btn-link p-0 d-inline-flex align-items-center"
            onClick={addCustom}
          >
            <i className="material-icons-outlined" aria-hidden="true" style={{ fontSize: 18 }}>
              add
            </i>
            Campo
          </button>
        </div>
      </div>

      <div className="mt-3 pt-3 border-top">
        <button type="button" className="btn btn-sm btn-primary mr-2" onClick={submit}>
          Salvar dados
        </button>
        <button type="button" className="btn btn-sm btn-link" onClick={() => setEditing(false)}>
          Cancelar
        </button>
      </div>
      {saveError && <div className="alert alert-danger mt-2 mb-0">{saveError}</div>}
    </>
  );

  if (error) {
    return (
      <div className="alert alert-warning">Não foi possível carregar os dados da empresa.</div>
    );
  }
  if (loading && !company) return <Loader />;

  return (
    <Container className="proposal-company">
      {monitored.length > 1 && (
        <label className="proposal-company-selector d-block mb-3" htmlFor="proposal-company-cnpj">
          <span className="small d-block">Empresa que vai apresentar a proposta</span>
          <select
            id="proposal-company-cnpj"
            className="form-control form-control-sm"
            value={cnpj ?? ''}
            onChange={(event) => {
              const { value } = event.target;
              setEditing(false);
              setCnpj(value);
            }}
          >
            {monitored.map((m) => (
              <option key={m.cnpj} value={m.cnpj}>
                {m.nome ? `${m.nome} — ${maskCnpj(m.cnpj)}` : maskCnpj(m.cnpj)}
              </option>
            ))}
          </select>
        </label>
      )}

      {savedFlash && <div className="alert alert-success py-2">Dados da empresa salvos</div>}

      <section className="form-card">
        <div className="section-head">
          <h2>
            <i className="material-icons-outlined" aria-hidden="true">
              business
            </i>
            Dados da empresa
          </h2>
          {!editing && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center"
              onClick={startEdit}
            >
              <i className="material-icons-outlined mr-1" aria-hidden="true">
                edit
              </i>
              Editar
            </button>
          )}
        </div>
        {editing ? renderEdicao() : renderLeitura()}

        {undoField && (
          <div className="proposal-company-undo alert alert-dark d-flex align-items-center justify-content-between py-2 mt-3">
            <span>Campo removido.</span>
            <button type="button" className="btn btn-sm btn-link p-0" onClick={undoRemoveCustom}>
              Desfazer
            </button>
          </div>
        )}

        {company && (
          <label
            className="proposal-company-confirm d-flex align-items-start"
            htmlFor="emp-confirmada"
            data-proposal-target={proposalFieldTarget('confirmacao')}
          >
            <input
              id="emp-confirmada"
              type="checkbox"
              className="mr-2 mt-1"
              checked={company.confirmada}
              onChange={(event) => {
                const { checked } = event.target;
                confirm(checked);
              }}
            />
            <span>
              Confirmo que os dados da empresa estão corretos
              {company.confirmada && company.confirmada_por?.nome && (
                <small className="d-block text-muted">
                  Confirmado por {company.confirmada_por.nome}
                </small>
              )}
            </span>
          </label>
        )}
      </section>
    </Container>
  );
};

export default AiManagementProposalCompany;
